import type {
  WatchSignalDefinition,
  WatchSignalFamily,
} from "../../lib/watchSignalCatalog";
import { getReviewedSignalPresenter } from "../../lib/watchSignalReleaseRegistry";
import WatchCatalogSignalDisclosure from "./WatchCatalogSignalDisclosure";

interface SelectedSignalIdentity {
  signalId: string;
  definitionVersion: number;
}

export interface WatchAdvancedSignalFamiliesProps {
  /** Already-authorized definitions from the reviewed public catalog. */
  signals: readonly WatchSignalDefinition[];
  selected: SelectedSignalIdentity | null;
  onSelect: (signal: WatchSignalDefinition) => void;
}

interface FamilyPresentation {
  family: WatchSignalFamily;
  title: string;
  description: string;
  legend: string;
  radioName: string;
  matches?: (signal: WatchSignalDefinition) => boolean;
}

const isPositioningSignal = (signal: WatchSignalDefinition) => {
  const presenter = getReviewedSignalPresenter(signal);
  return presenter?.kind === "STANDARDIZED_OPTION_CHAIN"
    && (presenter.measurement === "PUT_CALL_OPEN_INTEREST_7_45D"
      || presenter.measurement === "OPEN_INTEREST_CONCENTRATION_TOP5_7_45D");
};

const ADVANCED_FAMILIES: readonly FamilyPresentation[] = [
  {
    family: "VOLATILITY",
    title: "Options volatility",
    description: "Track implied volatility, implied versus recent volatility, or expected move.",
    legend: "Choose an options-volatility measurement",
    radioName: "advanced-volatility-measurement",
  },
  {
    family: "OPTIONS_CHAIN",
    title: "Skew & term structure",
    description: "Compare put and call volatility or the shape of volatility across time.",
    legend: "Choose a skew or term-structure measurement",
    radioName: "advanced-option-chain-measurement",
    matches: (signal) => !isPositioningSignal(signal),
  },
  {
    family: "OPTIONS_CHAIN",
    title: "Options positioning",
    description: "Track how reported open interest is split and concentrated across the standardized near-term cohort.",
    legend: "Choose an options-positioning measurement",
    radioName: "options-positioning-measurement",
    matches: isPositioningSignal,
  },
  {
    family: "LIQUIDITY",
    title: "Options liquidity",
    description: "Track quote coverage or the typical spread near the current price.",
    legend: "Choose a liquidity measurement",
    radioName: "advanced-liquidity-measurement",
  },
];

/**
 * Progressive-disclosure list for released advanced market conditions.
 *
 * Rendered inline as part of whichever intent chip its family maps to
 * ("How big could the move be?" for VOLATILITY, "Are options pricing it
 * richly?" for OPTIONS_CHAIN, "Can I get in and out easily?" for LIQUIDITY —
 * see WatchSignalPicker's SIGNAL_VIEWS) — it carries no heading of its own
 * so it reads as one browsing surface with the rest of that chip's cards
 * rather than a separate stacked section.
 *
 * This component intentionally has no fallback definitions. An empty reviewed
 * capability set produces no UI, so unreleased conditions remain default-hidden.
 */
export default function WatchAdvancedSignalFamilies({
  signals,
  selected,
  onSelect,
}: WatchAdvancedSignalFamiliesProps) {
  const availableFamilies = ADVANCED_FAMILIES.map((presentation) => ({
    presentation,
    signals: signals.filter((signal) => signal.family === presentation.family
      && (presentation.matches?.(signal) ?? true)),
  })).filter((group) => group.signals.length > 0);

  if (availableFamilies.length === 0) return null;

  return (
    <>
      {availableFamilies.map(({ presentation, signals: familySignals }) => (
        <WatchCatalogSignalDisclosure
          key={presentation.title}
          title={presentation.title}
          description={presentation.description}
          legend={presentation.legend}
          signals={familySignals}
          selected={selected}
          onSelect={onSelect}
          radioName={presentation.radioName}
        />
      ))}
    </>
  );
}
